import React, { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Star } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import EstoreChrome from "../components/EstoreChrome";
import { brand } from "../data/estoreContent";
import { estoreCatalog, effectivePrice } from "../data/estoreCatalog";
import { estoreProductUrl, estoreShopUrl, ESTORE_ROUTES } from "../data/estorePaths";

const allCategories = [...new Set(estoreCatalog.map((p) => p.category))].sort();

function EstoreShop() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get("search") || "";
  const [query, setQuery] = useState(initialSearch);
  const [category, setCategory] = useState("");
  const [sortBy, setSortBy] = useState("popular");

  useEffect(() => {
    const s = searchParams.get("search") || "";
    setQuery(s);
  }, [searchParams]);

  const filtered = useMemo(() => {
    let list = estoreCatalog.filter((p) => {
      const q = query.trim().toLowerCase();
      const matchQ =
        !q ||
        p.title.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.brand.toLowerCase().includes(q);
      const matchCat = !category || p.category === category;
      return matchQ && matchCat;
    });
    list = [...list].sort((a, b) => {
      if (sortBy === "priceAsc") return effectivePrice(a) - effectivePrice(b);
      if (sortBy === "priceDesc") return effectivePrice(b) - effectivePrice(a);
      return b.rating - a.rating;
    });
    return list;
  }, [query, category, sortBy]);

  const applySearchToUrl = () => {
    navigate(estoreShopUrl(query.trim()));
  };

  return (
    <Box sx={{ mt: 7, minHeight: "100vh", bgcolor: brand.bg }}>
      <EstoreChrome title="Shop" />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <Typography variant="h4" fontWeight={800} sx={{ color: brand.text, letterSpacing: "-0.02em", mb: 1 }}>
            Catalog
          </Typography>
          <Typography variant="body1" sx={{ color: brand.muted, mb: 3 }}>
            {filtered.length} pieces in this view
          </Typography>
        </motion.div>

        <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search title, brand, category…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applySearchToUrl()}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" sx={{ bgcolor: "#fff", borderRadius: 2 }}>
              <InputLabel>Sort</InputLabel>
              <Select label="Sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <MenuItem value="popular">Rating</MenuItem>
                <MenuItem value="priceAsc">Price ↑</MenuItem>
                <MenuItem value="priceDesc">Price ↓</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              <Chip
                label="All"
                onClick={() => setCategory("")}
                color={category === "" ? "primary" : "default"}
                variant={category === "" ? "filled" : "outlined"}
              />
              {allCategories.map((c) => (
                <Chip
                  key={c}
                  label={c}
                  onClick={() => setCategory(c === category ? "" : c)}
                  color={category === c ? "primary" : "default"}
                  variant={category === c ? "filled" : "outlined"}
                />
              ))}
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={2.5}>
          {filtered.map((p, i) => {
            const price = effectivePrice(p);
            return (
              <Grid item xs={6} md={4} lg={3} key={p.id}>
                <motion.div
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: Math.min(i * 0.03, 0.35) }}
                  whileHover={{ y: -4 }}
                >
                  <Card
                    elevation={0}
                    onClick={() => navigate(estoreProductUrl(p.id))}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${brand.stroke}`,
                      cursor: "pointer",
                      overflow: "hidden",
                      bgcolor: "#fff",
                    }}
                  >
                    <Box sx={{ pt: "100%", position: "relative", bgcolor: "#F1F5F9" }}>
                      <Box
                        component="img"
                        src={p.image}
                        alt={p.title}
                        sx={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      {p.discount > 0 && (
                        <Chip
                          label={`${p.discount}% off`}
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            fontWeight: 800,
                            bgcolor: "rgba(15,23,42,0.88)",
                            color: "#fff",
                          }}
                        />
                      )}
                    </Box>
                    <CardContent sx={{ p: 1.75 }}>
                      <Typography variant="subtitle2" fontWeight={800} noWrap>
                        {p.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: brand.muted }}>
                        {p.brand} · {p.category}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                        <Star size={14} fill="#FBBF24" color="#FBBF24" />
                        <Typography variant="caption">{p.rating.toFixed(1)}</Typography>
                      </Box>
                      <Typography variant="subtitle1" fontWeight={800} sx={{ mt: 0.5 }}>
                        ₹{price}
                        {p.discount > 0 && (
                          <Typography component="span" variant="caption" sx={{ textDecoration: "line-through", color: brand.muted, ml: 1 }}>
                            ₹{p.price}
                          </Typography>
                        )}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>

        {filtered.length === 0 && (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <Typography sx={{ color: brand.muted, mb: 2 }}>No matches — try clearing filters.</Typography>
            <Button variant="contained" onClick={() => { setQuery(""); setCategory(""); navigate(ESTORE_ROUTES.shop); }}>
              Reset
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default EstoreShop;
